import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ limitCallbacks: true });

const registry = new Map();
const desktopQuery = window.matchMedia('(min-width: 769px)');
const motionReduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function getSectionId(section) {
  const wrapper = section?.closest?.('.shopify-section') ?? section;
  return wrapper?.id || `sail-technology-scroll-${registry.size + 1}`;
}

function remember(section, animation) {
  if (!section || !animation) return animation;

  const sectionId = getSectionId(section);
  const entry = registry.get(sectionId) ?? [];

  entry.push(animation);
  registry.set(sectionId, entry);

  return animation;
}

function killAnimation(animation) {
  if (!animation) return;

  if (animation.scrollTrigger) {
    animation.scrollTrigger.kill();
  }

  if (typeof animation.kill === 'function') {
    animation.kill();
  }
}

function killSection(section) {
  const sectionId = getSectionId(section);
  const animations = registry.get(sectionId) ?? [];

  animations.forEach(killAnimation);
  registry.delete(sectionId);
}

function killAll() {
  registry.forEach((animations) => animations.forEach(killAnimation));
  registry.clear();
}

function isSailTechnologyPage() {
  const { pageType, pageHandle, templateSuffix } = window.themeVariables?.settings ?? {};

  if (pageType !== 'page') return false;
  if (pageHandle === 'sail-technology') return true;
  if (typeof templateSuffix === 'string' && templateSuffix.includes('sail-technology')) return true;

  return document.querySelectorAll('.tech-block').length >= 3;
}

function isEnabled() {
  const { enableScrollAnimations } = window.themeVariables?.settings ?? {};

  if (!isSailTechnologyPage()) return false;
  if (enableScrollAnimations === false) return false;
  if (motionReduceQuery.matches) return false;

  return true;
}

function setReducedMotionState() {
  document.body.classList.remove('js-sail-technology-scroll');

  gsap.set('.tech-block__background, .tech-block__content > *, .tech-video__container', {
    clearProps: 'all',
    opacity: 1,
    x: 0,
    y: 0,
    yPercent: 0,
    scale: 1,
  });
}

function getRevealDirection(block) {
  if (block.classList.contains('tech-block--pos-right')) return 48;
  if (block.classList.contains('tech-block--pos-center')) return 0;
  return -48;
}

function initTechBlock(block) {
  if (!block) return;

  const background = block.querySelector('.tech-block__background--parallax');
  const contentItems = block.querySelectorAll(
    '.tech-block__heading, .tech-block__description, .tech-block__subdesc, .tech-block__bullets'
  );

  if (background && desktopQuery.matches) {
    const parallax = gsap.fromTo(
      background,
      { yPercent: -8, scale: 1.08 },
      {
        yPercent: 8,
        scale: 1.08,
        ease: 'none',
        scrollTrigger: {
          trigger: block,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      }
    );

    remember(block, parallax);
  }

  if (!contentItems.length) return;

  const direction = getRevealDirection(block);
  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: block,
      start: 'top 72%',
      once: true,
    },
  });

  timeline.from(contentItems, {
    x: direction,
    y: direction === 0 ? 32 : 0,
    opacity: 0,
    stagger: 0.12,
    duration: 0.75,
    ease: 'power3.out',
  });

  remember(block, timeline);
}

function initTechVideo(section) {
  const container = section?.querySelector?.('.tech-video__container');
  if (!container) return;

  const tween = gsap.from(container, {
    scrollTrigger: {
      trigger: section,
      start: 'top 85%',
      once: true,
    },
    y: 40,
    scale: 0.96,
    opacity: 0,
    duration: 0.7,
    ease: 'power3.out',
  });

  remember(section, tween);
}

function initSection(root = document) {
  killSection(root);

  const blocks = root.matches?.('.tech-block')
    ? [root]
    : Array.from(root.querySelectorAll?.('.tech-block') ?? []);

  blocks.forEach(initTechBlock);

  const videos = root.matches?.('.tech-video')
    ? [root]
    : Array.from(root.querySelectorAll?.('.tech-video') ?? []);

  videos.forEach(initTechVideo);
}

function initSailTechnologyScroll(root = document) {
  if (root === document) {
    killAll();
  } else {
    killSection(root);
  }

  if (!isEnabled()) {
    killAll();
    setReducedMotionState();
    return;
  }

  document.body.classList.add('js-sail-technology-scroll');
  initSection(root);
  requestAnimationFrame(() => ScrollTrigger.refresh());
}

function debounce(fn, wait) {
  let timeout;

  return (...args) => {
    window.clearTimeout(timeout);
    timeout = window.setTimeout(() => fn(...args), wait);
  };
}

function refreshAfterMedia(root = document) {
  const media = root.querySelectorAll
    ? root.querySelectorAll('.tech-block__background, .tech-video__thumbnail, main img')
    : document.querySelectorAll('.tech-block__background, .tech-video__thumbnail, main img');

  media.forEach((item) => {
    if (item.tagName === 'IMG') {
      if (item.complete) return;
      item.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
      return;
    }

    const image = new Image();
    const backgroundImage = getComputedStyle(item).backgroundImage;

    if (!backgroundImage || backgroundImage === 'none') return;

    image.src = backgroundImage.slice(5, -2).replace(/"/g, '');
    if (image.complete) return;

    image.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSailTechnologyScroll();
  refreshAfterMedia();
});

window.addEventListener('load', () => ScrollTrigger.refresh());
window.addEventListener('resize', debounce(() => ScrollTrigger.refresh(), 150));

desktopQuery.addEventListener?.('change', () => {
  killAll();
  initSailTechnologyScroll();
});

motionReduceQuery.addEventListener?.('change', () => {
  killAll();
  initSailTechnologyScroll();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!isEnabled()) {
    setReducedMotionState();
    return;
  }

  initSection(event.target);
  refreshAfterMedia(event.target);
  requestAnimationFrame(() => ScrollTrigger.refresh());
});

document.addEventListener('shopify:section:unload', (event) => {
  killSection(event.target);
  requestAnimationFrame(() => ScrollTrigger.refresh());
});

export { remember };
